import React, { useEffect, useState } from 'react';
import { IEmpresaQsaItem, IEmpresaQsaSalvar } from '../interfaces/IEmpresaQsa';
import { QsaService } from '../services/QsaService';

interface Props {
  empresaId: number;
  nomeEmpresa?: string;
  onFechar: () => void;
  onQsaAtualizado: () => void;
}

const vazio: IEmpresaQsaSalvar = { nome: '', qualificacao: '', cpfCnpj: '' };

const QsaGerenciarModal: React.FC<Props> = ({ empresaId, nomeEmpresa, onFechar, onQsaAtualizado }) => {
  const [socios, setSocios] = useState<IEmpresaQsaItem[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [sincronizando, setSincronizando] = useState(false);
  const [mensagemSinc, setMensagemSinc] = useState<string | null>(null);
  const [mensagemSincTecnica, setMensagemSincTecnica] = useState<string | null>(null);
  const [editando, setEditando] = useState<IEmpresaQsaItem | null>(null);
  const [adicionando, setAdicionando] = useState(false);
  const [form, setForm] = useState<IEmpresaQsaSalvar>(vazio);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = async () => {
    setCarregando(true);
    try {
      const lista = await QsaService.listar(empresaId);
      setSocios(lista.socios || []);
    } catch {
      setErro('Erro ao carregar o QSA.');
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => { carregar(); }, [empresaId]);

  const sincronizar = async () => {
    setSincronizando(true);
    setMensagemSinc(null);
    setMensagemSincTecnica(null);
    try {
      const resultado = await QsaService.sincronizar(empresaId);
      setMensagemSinc(resultado.mensagem);
      setMensagemSincTecnica(resultado.mensagemTecnica || null);
      if (resultado.sucesso) {
        await carregar();
        onQsaAtualizado();
      }
    } catch {
      setMensagemSinc('Erro ao tentar sincronizar o QSA via fonte pública.');
    } finally {
      setSincronizando(false);
    }
  };

  const abrirAdicionar = () => {
    setAdicionando(true);
    setEditando(null);
    setForm(vazio);
    setErro(null);
  };

  const abrirEditar = (socio: IEmpresaQsaItem) => {
    setEditando(socio);
    setAdicionando(false);
    setForm({ nome: socio.nome, qualificacao: socio.qualificacao || '', cpfCnpj: socio.cpfCnpj || '' });
    setErro(null);
  };

  const cancelarFormulario = () => {
    setAdicionando(false);
    setEditando(null);
    setForm(vazio);
    setErro(null);
  };

  const salvar = async () => {
    if (!form.nome.trim()) { setErro('O nome é obrigatório.'); return; }
    setSalvando(true);
    setErro(null);
    try {
      if (editando) {
        await QsaService.atualizar(empresaId, editando.id, form);
      } else {
        await QsaService.adicionar(empresaId, form);
      }
      await carregar();
      cancelarFormulario();
      onQsaAtualizado();
    } catch {
      setErro('Erro ao salvar o sócio/administrador.');
    } finally {
      setSalvando(false);
    }
  };

  const remover = async (socioId: number) => {
    if (!window.confirm('Remover este sócio/administrador do QSA?')) return;
    try {
      await QsaService.remover(empresaId, socioId);
      await carregar();
      onQsaAtualizado();
    } catch {
      setErro('Erro ao remover o sócio/administrador.');
    }
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)',
        zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onFechar(); }}
    >
      <div style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 640, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 64px rgba(0,0,0,.18)' }}>

        {/* Cabeçalho */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #eef0f4', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 16, color: '#1f2a44' }}>
              <i className='bi bi-people-fill me-2 text-primary' />
              Quadro de Sócios e Administradores (QSA)
            </div>
            {nomeEmpresa && <div style={{ color: '#6b7280', fontSize: 13, marginTop: 2 }}>{nomeEmpresa}</div>}
          </div>
          <button className='btn btn-sm btn-light' onClick={onFechar}>
            <i className='bi bi-x-lg' />
          </button>
        </div>

        {/* Corpo */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '18px 22px' }}>
          {/* Sincronização via API pública */}
          <div style={{ background: '#f8fafc', border: '1px solid #eef0f4', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
            <div style={{ fontSize: 13, color: '#374151', marginBottom: 8 }}>
              <i className='bi bi-cloud-download me-2 text-primary' />
              <strong>Sincronizar via fonte pública</strong> — tenta obter o QSA da Receita Federal via BrasilAPI / ReceitaWS e salvar como cache.
            </div>
            <button className='btn btn-sm btn-primary' onClick={sincronizar} disabled={sincronizando}>
              {sincronizando ? <><span className='spinner-border spinner-border-sm me-2' />Sincronizando…</> : <><i className='bi bi-arrow-clockwise me-2' />Sincronizar agora</>}
            </button>
            {mensagemSinc && (
              <div style={{ marginTop: 8, fontSize: 12 }}>
                <span style={{ color: mensagemSinc.toLowerCase().includes('não foi') || mensagemSinc.toLowerCase().includes('inválido') ? '#9b1c1c' : '#126b3f' }}>
                  {mensagemSinc}
                </span>
                {mensagemSincTecnica && (
                  <details style={{ marginTop: 4 }}>
                    <summary style={{ cursor: 'pointer', color: '#6b7280', fontSize: 11, userSelect: 'none' }}>
                      <i className='bi bi-bug me-1' />Detalhe técnico
                    </summary>
                    <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace', marginTop: 4 }}>
                      {mensagemSincTecnica}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>

          {/* Erro geral */}
          {erro && <div className='alert alert-danger py-2' style={{ fontSize: 13 }}>{erro}</div>}

          {/* Formulário de adição/edição */}
          {(adicionando || editando) && (
            <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontWeight: 700, marginBottom: 10, color: '#0369a1' }}>
                <i className={`bi ${editando ? 'bi-pencil' : 'bi-person-plus-fill'} me-2`} />
                {editando ? 'Editar sócio/administrador' : 'Adicionar sócio/administrador'}
              </div>
              <div className='mb-2'>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Nome *</label>
                <input
                  className='form-control form-control-sm'
                  value={form.nome}
                  onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  placeholder='Nome completo'
                />
              </div>
              <div className='mb-2'>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>Qualificação</label>
                <input
                  className='form-control form-control-sm'
                  value={form.qualificacao || ''}
                  onChange={(e) => setForm({ ...form, qualificacao: e.target.value })}
                  placeholder='Ex: Sócio-Administrador, Diretor…'
                />
              </div>
              <div className='mb-3'>
                <label style={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>CPF/CNPJ</label>
                <input
                  className='form-control form-control-sm'
                  value={form.cpfCnpj || ''}
                  onChange={(e) => setForm({ ...form, cpfCnpj: e.target.value })}
                  placeholder='Opcional'
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className='btn btn-sm btn-primary' onClick={salvar} disabled={salvando}>
                  {salvando ? <span className='spinner-border spinner-border-sm' /> : <><i className='bi bi-check2 me-1' />{editando ? 'Salvar alterações' : 'Adicionar'}</>}
                </button>
                <button className='btn btn-sm btn-light' onClick={cancelarFormulario}>Cancelar</button>
              </div>
            </div>
          )}

          {/* Lista de sócios */}
          {carregando ? (
            <div className='text-center py-4'><span className='spinner-border text-primary' /></div>
          ) : socios.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '28px 0', color: '#6b7280' }}>
              <i className='bi bi-people' style={{ fontSize: 32, display: 'block', marginBottom: 8 }} />
              Nenhum sócio/administrador cadastrado.
              <br />
              <span style={{ fontSize: 12 }}>Use "Sincronizar" ou "Adicionar" para cadastrar o QSA.</span>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: '#5b5f7a', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700 }}>Nome</th>
                  <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700 }}>Qualificação</th>
                  <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700 }}>Fonte</th>
                  <th style={{ padding: '8px 10px' }} />
                </tr>
              </thead>
              <tbody>
                {socios.map((s) => (
                  <tr key={s.id} style={{ borderBottom: '1px solid #eef0f4' }}>
                    <td style={{ padding: '10px', fontWeight: 600, color: '#1f2a44' }}>
                      {s.nome}
                      {s.cpfCnpj && <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 400 }}>{s.cpfCnpj}</div>}
                    </td>
                    <td style={{ padding: '10px', color: '#374151' }}>{s.qualificacao || '—'}</td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      <span style={{
                        display: 'inline-block', padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                        background: s.fonte === 'MANUAL' ? '#eef2ff' : '#f0fdf4',
                        color: s.fonte === 'MANUAL' ? '#3730a3' : '#126b3f',
                      }}>
                        {s.fonte === 'MANUAL' ? 'Manual' : 'Cache API'}
                      </span>
                    </td>
                    <td style={{ padding: '10px', textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className='btn btn-sm btn-light me-1' title='Editar' onClick={() => abrirEditar(s)}>
                        <i className='bi bi-pencil' />
                      </button>
                      <button className='btn btn-sm btn-light text-danger' title='Remover' onClick={() => remover(s.id)}>
                        <i className='bi bi-trash' />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Rodapé */}
        <div style={{ padding: '12px 22px', borderTop: '1px solid #eef0f4', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {!adicionando && !editando && (
            <button className='btn btn-sm btn-outline-primary' onClick={abrirAdicionar}>
              <i className='bi bi-person-plus-fill me-2' />Adicionar sócio
            </button>
          )}
          <button className='btn btn-sm btn-light ms-auto' onClick={onFechar}>Fechar</button>
        </div>
      </div>
    </div>
  );
};

export default QsaGerenciarModal;
